import { useSearchParams } from "react-router-dom"
import { useRef, useMemo, useCallback } from "react"

export function useTableParams<T extends Record<string, any>>(
  defaultParams: T,
  options?: { replace?: boolean }
): [T, (newParams: Partial<T> | ((prev: T) => T), opts?: { replace?: boolean }) => void] {
  const [searchParams, setSearchParams] = useSearchParams()

  // Use a ref to store defaultParams so we don't depend on its reference
  const defaultParamsRef = useRef(defaultParams)
  defaultParamsRef.current = defaultParams

  const params = useMemo(() => {
    const defaults = defaultParamsRef.current
    const currentParams: Record<string, any> = { ...defaults }

    for (const key of Object.keys(defaults)) {
      const value = searchParams.get(key)
      if (value !== null && value !== "") {
        // Parse type based on defaultParams types
        const defaultType = typeof defaults[key]
        const isNumeric =
          defaultType === "number" ||
          key === "user_id" ||
          key === "is_active" ||
          key.endsWith("_id") ||
          (key === "status" && /^-?\d+$/.test(value))
        if (isNumeric) {
          const num = Number(value)
          /* biome-ignore lint/suspicious/noExplicitAny: type coercion needs any */
          currentParams[key] = (Number.isNaN(num) ? defaults[key] : num) as any
        } else if (defaultType === "boolean") {
          /* biome-ignore lint/suspicious/noExplicitAny: type coercion needs any */
          currentParams[key] = (value === "true") as any
        } else {
          /* biome-ignore lint/suspicious/noExplicitAny: type coercion needs any */
          currentParams[key] = value as any
        }
      } else if (value === "") {
        // Keep empty string if default is a string, otherwise delete/set undefined
        if (typeof defaults[key] === "string") {
          /* biome-ignore lint/suspicious/noExplicitAny: type coercion needs any */
          currentParams[key] = "" as any
        } else {
          /* biome-ignore lint/suspicious/noExplicitAny: type coercion needs any */
          currentParams[key] = undefined as any
        }
      }
    }
    return currentParams as T
  }, [searchParams])

  const setParams = useCallback(
    (newParams: Partial<T> | ((prev: T) => T), opts?: { replace?: boolean }) => {
      setSearchParams(
        (prevSearchParams) => {
          const defaults = defaultParamsRef.current
          const current: Record<string, any> = { ...defaults }
          for (const key of Object.keys(defaults)) {
            const value = prevSearchParams.get(key)
            if (value !== null && value !== "") {
              const defaultType = typeof defaults[key]
              const isNumeric =
                defaultType === "number" ||
                key === "user_id" ||
                key === "is_active" ||
                key.endsWith("_id") ||
                (key === "status" && /^-?\d+$/.test(value))
              if (isNumeric) {
                const num = Number(value)
                /* biome-ignore lint/suspicious/noExplicitAny: type coercion needs any */
                current[key] = (Number.isNaN(num) ? defaults[key] : num) as any
              } else if (defaultType === "boolean") {
                /* biome-ignore lint/suspicious/noExplicitAny: type coercion needs any */
                current[key] = (value === "true") as any
              } else {
                /* biome-ignore lint/suspicious/noExplicitAny: type coercion needs any */
                current[key] = value as any
              }
            } else if (value === "") {
              if (typeof defaults[key] === "string") {
                /* biome-ignore lint/suspicious/noExplicitAny: type coercion needs any */
                current[key] = "" as any
              } else {
                /* biome-ignore lint/suspicious/noExplicitAny: type coercion needs any */
                current[key] = undefined as any
              }
            }
          }

          const next = typeof newParams === "function" ? newParams(current as T) : newParams
          const updated = { ...current, ...next }

          const nextSearchParams = new URLSearchParams(prevSearchParams)
          for (const [key, val] of Object.entries(updated)) {
            if (val === undefined || val === null || val === "") {
              nextSearchParams.delete(key)
            } else {
              nextSearchParams.set(key, String(val))
            }
          }
          return nextSearchParams
        },
        { replace: opts?.replace !== undefined ? opts.replace : (options?.replace ?? true) }
      )
    },
    [setSearchParams, options?.replace]
  )

  return [params, setParams]
}
